import { AuthenticatorType, GeneralAuthenticatorType, User } from '@mews-commander/data-access';
import { LocalizedTypography } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Button, Card, DropDownButton, DropdownItem, Stack, Typography } from '@optimus-web/core';
import { useCurrentUser } from '@mews-commander/core';
import { isAuthenticatorSetupEnforced } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { useCommanderSelector } from '@mews-commander/utils';
import { Configure2FAPanel, Configure2FAPanelRoutes, useConfigure2FAPanel } from '../../../../auth';
import { usePasswordGatedCallback, usePasswordGatedDialog } from '../VerifyPasswordDialog';
import { TagWithStatus } from '../TagWithStatus';
import { MFA_DISABLE_DIALOG_NAME, MFADisableDialog } from './MFADisableDialog';
import { MFA_RESET_DIALOG_NAME, MFAResetDialog } from './MFAResetDialog';

export const AuthenticatorSetting = ({ currentUser }: { currentUser?: User }) => {
    const { authenticator } = useCurrentUser();
    const isEnforcedFromEnterprise = useCommanderSelector(isAuthenticatorSetupEnforced);
    const isEnforced = isEnforcedFromEnterprise && (!currentUser || currentUser.mfaEnforced === true);

    const authenticationEnabled = !!authenticator && authenticator !== GeneralAuthenticatorType.None;

    return (
        <Card padding="300">
            <Stack distribution="space-between" itemAlignment="start" spacing="200">
                <Stack vertical distribution="space-between" spacing="200">
                    <LocalizedTypography
                        textKey={TextKey.TwoFactorAuthentication2FA}
                        textStyle="titleSmallStrong"
                        as="div"
                    />
                    <Stack vertical>
                        <AuthenticatorSettingDescription authenticator={authenticator} />
                        {isEnforced && <LocalizedText textKey={TextKey.TwoFactorAuthenticationEnforceWarning} />}
                    </Stack>

                    <Stack>
                        <AvailableActions isEnforced={isEnforced} authenticator={authenticator} />
                    </Stack>
                </Stack>
                <TagWithStatus isEnabled={authenticationEnabled} />
            </Stack>
            <Configure2FAPanel />
        </Card>
    );
};

const commonButtonProps = { size: 'small' as const };

const methodTextKeys = {
    [AuthenticatorType.Email]: TextKey.TwoFactorAuthenticationEmailCode,
    [AuthenticatorType.Google]: TextKey.TwoFactorAuthenticationApp,
};

const AuthenticatorSettingDescription = ({ authenticator }: { authenticator: AuthenticatorType }) => {
    if (!authenticator || authenticator === GeneralAuthenticatorType.None) {
        return (
            <Banner
                primaryText={<LocalizedText textKey={TextKey.TwoFactorAuthentication2FADescription} />}
                variant="warning"
            />
        );
    }

    return (
        <Typography textStyle="bodyMedium" as="div">
            <LocalizedText
                textKey={TextKey.AccountSecuredWith}
                substitutes={{
                    method: <LocalizedText textKey={methodTextKeys[authenticator]} />,
                    strong: (text) => <strong>{text}</strong>,
                }}
            />
        </Typography>
    );
};

const AvailableActions = ({ authenticator, isEnforced }: { authenticator: AuthenticatorType; isEnforced: boolean }) => {
    const { handleEnable, handleDisable } = useAuthenticatorActions();

    if (authenticator === AuthenticatorType.None) {
        return <Configure2FAButton authenticator={AuthenticatorType.None} />;
    }

    if (isEnforced) {
        return <Configure2FAButton authenticator={authenticator} />;
    }

    return (
        <DropDownButton label={<LocalizedText textKey={TextKey.Manage} />} size="small">
            <DropdownItem label={<Configure2FALabel authenticator={authenticator} />} onClick={handleEnable} />
            <DropdownItem label={<LocalizedText textKey={TextKey.Deactivate} />} onClick={handleDisable} />
        </DropDownButton>
    );
};

const Configure2FAButton = ({ authenticator }: { authenticator: AuthenticatorType }) => {
    const { handleEnable, handleConfigure } = useAuthenticatorActions();

    if (authenticator === GeneralAuthenticatorType.None) {
        return (
            <Button {...commonButtonProps} onClick={handleConfigure} type="submit">
                <Configure2FALabel authenticator={authenticator} />
            </Button>
        );
    }

    return (
        <Button {...commonButtonProps} onClick={handleEnable} type="submit">
            <Configure2FALabel authenticator={authenticator} />
        </Button>
    );
};

const Configure2FALabel = ({ authenticator }: { authenticator: AuthenticatorType }) => {
    if (authenticator === GeneralAuthenticatorType.Google) {
        return <LocalizedText textKey={TextKey.SettingsSecuritySwitchToEmailVerification} />;
    }

    if (authenticator === GeneralAuthenticatorType.Email) {
        return <LocalizedText textKey={TextKey.SettingsSecuritySwitchToAuthenticatorApp} />;
    }

    return <LocalizedText textKey={TextKey.Configure2FAShort} />;
};

function useAuthenticatorActions() {
    const { open2FAPanel } = useConfigure2FAPanel();
    const openPasswordGatedDialog = usePasswordGatedDialog();
    const openPasswordGatedCallback = usePasswordGatedCallback();

    const handleEnable = () => {
        openPasswordGatedDialog(
            MFAResetDialog,
            MFA_RESET_DIALOG_NAME,
            TextKey.TwoFactorAuthenticationPasswordGateEnableMessage
        );
    };
    const handleDisable = () =>
        openPasswordGatedDialog(
            MFADisableDialog,
            MFA_DISABLE_DIALOG_NAME,
            TextKey.TwoFactorAuthenticationPasswordGateDisableMessage
        );

    const handleConfigure = () =>
        openPasswordGatedCallback({
            onSuccess: (password) => open2FAPanel(Configure2FAPanelRoutes.selectMethod, password),
            descriptionTextKey: TextKey.TwoFactorAuthenticationPasswordGateDisableMessage,
        });

    return {
        handleDisable,
        handleEnable,
        handleConfigure,
    };
}
